import type { FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { checkHasLiveTag } from "scmp-app/components/article/article-render/helpers";
import { checkIsPlusArticle } from "scmp-app/components/article/article-render/plus/helpers";
import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { LinkHighlightArea } from "scmp-app/components/content/content-item-render/highlight/styles";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import { checkHasSeriesShortLabel } from "scmp-app/components/content/content-series-short-label/helpers";
import { getContentSponsor } from "scmp-app/components/content/content-sponsor-tag/helpers";
import { usePgType, usePlusQueryParameters } from "scmp-app/components/content/helpers";
import { PlusFlag } from "scmp-app/components/plus/plus-flag";
import type { homePrimaryContentItemContent$key } from "scmp-app/queries/__generated__/homePrimaryContentItemContent.graphql";

import {
  ActionBar,
  Container,
  Content,
  CoverImage,
  Header,
  Headline,
  LiveTagContainer,
  SectionContainer,
  SponsorTagContainer,
  StyledEntityLink,
  Summary,
  TopicLabel,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  currentTopicReference?: string;
  isReadingTimeEnabled?: boolean;
  reference: homePrimaryContentItemContent$key;
  withBookmark?: boolean;
  withCaption?: boolean;
  withCarousel?: boolean;
  withComment?: boolean;
  withLiveTag?: boolean;
  withSection?: boolean;
  withSeriesShortLabel?: boolean;
  withTopic?: boolean;
};

const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  currentTopicReference,
  isReadingTimeEnabled,
  onClick,
  ref: forwardReference,
  reference: reference_,
  topicLinkVariant,
  withBookmark = false,
  withCaption = true,
  withCarousel = false,
  withComment = false,
  withLiveTag = false,
  withSection = true,
  withSeriesShortLabel = false,
  withTopic = false,
}) => {
  const content = useFragment(
    graphql`
      fragment homePrimaryContentItemContent on Content {
        entityId
        urlAlias
        ...entityLink
        ...helpersCheckIsPlusArticle
        ...helpersCheckHasLiveTagArticle
        ...helpersCheckHasSeriesShortLabelContent
        ...helpersUseContentSponsorContent
        ...hooksContentItemProvidersContent
          @arguments(
            withSections: true
            withHeadline: true
            withLiveTag: true
            withSummary: true
            withActionBar: true
            withCoverImage: true
            withCoverImageSize1200x800: true
            withCoverImageSize540x360: true
            withCoverImageLeadingSlides: true
            withSponsorTag: true
            withVideoDurationOverlay: true
            withTopicLink: true
            withSeriesShortLabel: true
          )
      }
    `,
    reference_,
  );

  const providers = useContentItemProviders(content, topicLinkVariant);
  const hasLiveTag = checkHasLiveTag(content);
  const isShowLiveTag = hasLiveTag && withLiveTag;

  const contentSponsor = getContentSponsor(content);
  const isSponsorArticle = notEmpty(contentSponsor?.name);
  const isPlusArticle = checkIsPlusArticle(content);
  const hasSeriesShortLabel = checkHasSeriesShortLabel(content);

  const pgtype = usePgType();
  const plusQueryParameters = usePlusQueryParameters(content);

  const renderHeader = () => {
    switch (true) {
      case isShowLiveTag:
        return <LiveTagContainer>{providers.liveTag()}</LiveTagContainer>;
      case withTopic:
        return (
          <TopicLabel>
            {providers.topicLink?.({
              currentTopicReference,
              preferShortName: true,
            })}
          </TopicLabel>
        );
      case withSection:
        return (
          <SectionContainer>
            {withSeriesShortLabel && hasSeriesShortLabel
              ? providers.seriesShortLabel?.()
              : providers.sections()}
          </SectionContainer>
        );
      default:
        return null;
    }
  };

  return (
    <Container className={className} ref={forwardReference}>
      <Content>
        <Header>
          {isPlusArticle && <PlusFlag pgtype={pgtype} />}
          {renderHeader()}
        </Header>
        <StyledEntityLink
          onClick={() => onClick?.(content.entityId, content.urlAlias)}
          query={plusQueryParameters}
          reference={content}
        >
          <LinkHighlightArea>
            <Headline>
              {providers.headline({ hideTooltip: true, preferSocialHeadline: true })}
            </Headline>
            <Summary>
              {providers.summary({
                isTextOverflowEllipsis: true,
              })}
            </Summary>
          </LinkHighlightArea>
        </StyledEntityLink>
        {isSponsorArticle ? (
          <SponsorTagContainer>{providers.sponsorTag?.({ variant: "home" })}</SponsorTagContainer>
        ) : (
          <ActionBar>
            {providers.actionBar({
              commentVariant: "reverse",
              displayDateVariant: isReadingTimeEnabled ? "readingTime" : "updatedDate",
              isBookmarkVisible: withBookmark ?? false,
              isCommentsVisible: withComment ?? false,
            })}
          </ActionBar>
        )}
      </Content>
      <CoverImage>
        <StyledEntityLink
          onClick={() => onClick?.(content.entityId, content.urlAlias)}
          query={plusQueryParameters}
          reference={content}
        >
          {providers.coverImage({
            responsiveVariants: {
              desktopUp: "size1200x800",
              mobileUp: "size540x360",
              tabletUp: "size1200x800",
            },
            withCaption,
            withCarousel: withCarousel ? { imageType: "default" } : undefined,
          })}
          {providers.video?.durationOverlay({
            iconSize: "large",
            variant: "homepage",
          })}
        </StyledEntityLink>
      </CoverImage>
    </Container>
  );
};

Component.displayName = "ContentItemHomePrimary";

export const ContentItemHomePrimary = withHighlightedHeadline(Component);
